import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, BookOpen } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { useFavorites } from "@/lib/favorites";
import SmartImage from "@/components/SmartImage";

export default function Favorites() {
  const { favorites, remove } = useFavorites();
  const navigate = useNavigate();

  if (favorites.length === 0) return null;

  return (
    <section data-testid="favorites" className="mx-auto max-w-6xl px-6 py-12">
      <p className="mb-2 text-center font-sans text-xs uppercase tracking-[0.25em] text-primary">
        <Heart className="mr-1 inline h-3.5 w-3.5 fill-current" /> Your Favorites
      </p>
      <h2 className="mb-8 text-center font-serif text-3xl tracking-tight text-foreground sm:text-4xl">
        Answers you saved
      </h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        <AnimatePresence>
          {favorites.map((it) => (
            <motion.div
              key={it.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="group relative flex aspect-[4/5] flex-col justify-end overflow-hidden rounded-md border border-border shadow-sm"
            >
              <button
                data-testid="favorite-open"
                onClick={() => navigate(`/a/${it.id}`)}
                className="absolute inset-0 z-0 h-full w-full"
                aria-label={`Open ${it.title}`}
              >
                {it.image_url ? (
                  <SmartImage src={`${BACKEND_URL}${it.image_url}`} alt={it.title} className="h-full w-full" imgClassName="object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted"><BookOpen className="h-8 w-8 text-muted-foreground" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
              </button>
              <div className="pointer-events-none relative z-10 p-4">
                <p className="font-serif text-lg leading-tight tracking-tight text-white">{it.title}</p>
                <p className="mt-1 line-clamp-2 font-sans text-xs text-white/70">{it.question}</p>
              </div>
              <button
                data-testid="favorite-remove"
                onClick={() => remove(it.id)}
                className="absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-destructive"
                aria-label="Remove from favorites"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}
